import React from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Shared "Select date range" control — same react-datepicker setup already used
// on the Extraction Accuracy dashboard, factored out so every date-range picker
// in the app renders identically instead of each page wiring its own.

export interface DateRange {
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (v: DateRange) => void;
  minDate?: Date;
}

function toLocalIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function DateRangePicker({ value, onChange, minDate }: DateRangePickerProps) {
  return (
    <DatePicker
      selectsRange
      startDate={value.from ? new Date(value.from + 'T00:00:00') : null}
      endDate={value.to ? new Date(value.to + 'T00:00:00') : null}
      onChange={(update: [Date | null, Date | null]) => {
        const [start, end] = update;
        onChange({ from: start ? toLocalIso(start) : undefined, to: end ? toLocalIso(end) : undefined });
      }}
      minDate={minDate}
      placeholderText="Select date range"
      dateFormat="MMM d, yyyy"
      isClearable
      monthsShown={1}
      className="px-3 py-1.5 text-xs text-navy border border-trim rounded-lg bg-white w-52 cursor-pointer"
      // Portal the calendar to <body> — otherwise it renders inline near the
      // input and gets covered by any sticky table header / z-indexed row on
      // the page (every consuming page has one). react-datepicker.css sets its
      // own z-index on .react-datepicker-popper and loads after Tailwind's
      // utilities, so a plain z-[300] loses that cascade fight — needs !important.
      popperContainer={({ children }) => createPortal(children, document.body)}
      popperClassName="!z-[300]"
    />
  );
}
